import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductPicker } from "@/components/ProductPicker";
import { useProducts } from "@/hooks/useData";
import { api, errMsg, notifyStockChanged } from "@/lib/api";
import { todayStr } from "@/lib/format";
import { cn } from "@/lib/utils";

const blank = () => ({ product_id: "", date: todayStr(), warehouse: "A", type: "IN", qty: "", ref: "", note: "" });

export const QuickEntryDialog = ({ open, onOpenChange, defaults }) => {
  const { products } = useProducts();
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ ...blank(), ...defaults }); }, [open, defaults]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id || !(Number(form.qty) > 0)) return toast.error("Select a product and enter quantity");
    setBusy(true);
    try {
      if (form.type === "TRANSFER") await api.post("/movements/transfer", { product_id: form.product_id, date: form.date, qty: Number(form.qty), ref: form.ref, note: form.note });
      else await api.post("/movements", { ...form, warehouse: form.type === "OUT" ? "A" : form.warehouse, qty: Number(form.qty) });
      toast.success(form.type === "TRANSFER" ? "Stock moved from WH B to WH A" : `Stock ${form.type} recorded`);
      notifyStockChanged();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="quick-entry-dialog">
        <DialogHeader><DialogTitle className="font-display">Quick stock entry</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {[["IN", "↓ INWARD", "bg-emerald-600 border-emerald-500"], ["OUT", "↑ OUT (WH A)", "bg-red-600 border-red-500"], ["TRANSFER", "B → A", "bg-blue-600 border-blue-500"]].map(([t, label, on]) => (
              <button type="button" key={t} onClick={() => set("type")(t)} data-testid={`quick-type-${t.toLowerCase()}`}
                className={cn("h-10 rounded-md border text-sm font-bold tracking-wider transition-colors",
                  form.type === t ? `${on} text-white` : "border-border text-muted-foreground hover:bg-muted")}>
                {label}
              </button>
            ))}
          </div>
          <div className="space-y-1.5"><Label>Product</Label>
            <ProductPicker products={products} value={form.product_id} onChange={set("product_id")} testid="quick-product" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Date</Label>
              <Input type="date" value={form.date} onChange={set("date")} data-testid="quick-date" /></div>
            <div className="space-y-1.5"><Label>Warehouse</Label>
              {form.type === "IN" ? (
                <Select value={form.warehouse} onValueChange={set("warehouse")}>
                  <SelectTrigger data-testid="quick-warehouse"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="A">Warehouse A</SelectItem><SelectItem value="B">Warehouse B</SelectItem></SelectContent>
                </Select>
              ) : (
                <div className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm" data-testid="quick-warehouse-fixed">{form.type === "OUT" ? "WH A" : "B → A"}</div>
              )}</div>
            <div className="space-y-1.5"><Label>Quantity</Label>
              <Input type="number" min="1" value={form.qty} onChange={set("qty")} className="font-mono" data-testid="quick-qty" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Reference (invoice / PO)</Label>
              <Input value={form.ref} onChange={set("ref")} data-testid="quick-ref" /></div>
            <div className="space-y-1.5"><Label>Note</Label>
              <Input value={form.note} onChange={set("note")} data-testid="quick-note" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="quick-submit-btn">{busy ? "Saving…" : "Save entry"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
